import type { MouseEvent } from 'react';
import { useState, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { useAppState } from '../../../AppStateProvider';
import { Dropdown } from '../../../components/Dropdown';
import type { NewPersona, Persona } from '../../../domain/persona';
import { createPersona } from '../../../domain/persona';
import { usePersonasQuery } from '../../../features/persona/personasQuery/usePersonasQuery';
import { appStore } from '../../../services/appStore';

import { PersonaModal } from './PersonaModal';
import { PersonaTooltip } from './PersonaTooltip';
import {
  StyledFilterContainer,
  StyledCreateOption,
  StyledEditButton,
  StyledSharedIcon,
  StyledPersonaOption,
  StyledCreateOptionDescription,
  StyledSeparator,
  StyledPersonaDropdownButton,
  StyledPersonaDropdownButtonLabel,
  StyledPersonaName,
} from './styles';
import { usePersonaFilterTracking } from './usePersonaFilterTracking';

const DD_OPTION_HEIGHT = 44;

export const PersonaFilter = ({ isDisabled }: { isDisabled?: boolean }) => {
  const {
    contactFilters: { persona: selectedPersona },
  } = useAppState();
  const [isOpened, setIsOpened] = useState(false);
  const [persona, setPersona] = useState<Persona | NewPersona | null>(null);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();

  const { data: personas = [] } = usePersonasQuery();

  const { handleTrackOnOpen, handleTrackOnCreate, handleTrackOnEdit, handleTrackOnPersonaSelect } =
    usePersonaFilterTracking(personas);

  const handleClick = () => {
    setIsOpened(true);
    handleTrackOnOpen();
  };
  const handleCloseDropdown = useCallback(() => {
    setIsOpened(false);
  }, []);

  const filteredPersonas = personas.filter((persona) => persona.name.toLowerCase().includes(searchText.toLowerCase()));

  const handleClickOnIcon = (event: MouseEvent<HTMLDivElement>): void => {
    if (!selectedPersona) {
      return;
    }

    event.stopPropagation();
    appStore.setContactsPersona(undefined);
  };

  const handleClickOnCreate = () => {
    setIsOpened(false);
    setPersona(createPersona());
    handleTrackOnCreate();
  };

  const handleClickOnEdit = (event: MouseEvent, persona: Persona) => {
    event.stopPropagation();
    setIsOpened(false);
    setPersona(persona);
    handleTrackOnEdit(persona);
  };

  const handlePersonaSelect = (persona: Persona) => {
    appStore.setContactsPersona(persona);
    setIsOpened(false);
    handleTrackOnPersonaSelect(persona);
  };

  return (
    <StyledFilterContainer data-automation="PersonaFilter">
      <StyledPersonaDropdownButton
        onClick={handleClick}
        isSelected={!!selectedPersona}
        isActive={isOpened}
        disabled={isDisabled}
        data-automation="PersonaFilterDdButton"
      >
        <StyledPersonaDropdownButtonLabel>
          {selectedPersona ? selectedPersona.name : t('contacts.persona.empty')}
        </StyledPersonaDropdownButtonLabel>
        <div onClick={handleClickOnIcon}>
          <SWReactIcons
            iconName={selectedPersona ? 'clear-circle' : isOpened ? 'chev-up' : 'chev-down'}
            size={selectedPersona ? 'sm' : 'xs'}
          />
        </div>
      </StyledPersonaDropdownButton>
      <Dropdown
        isOpened={isOpened}
        onClose={handleCloseDropdown}
        onCloseFinish={() => setSearchText('')}
        searchProps={
          personas.length === 0
            ? undefined
            : {
                placeholder: t('contacts.persona.search') || '',
                value: searchText,
                onChange: (e) => setSearchText(e.target.value),
              }
        }
        data-automation="PersonaFilterDd"
        maxHeight={DD_OPTION_HEIGHT * 7}
      >
        <StyledCreateOption onClick={handleClickOnCreate} data-automation="PersonaFilterCreate">
          <SWReactIcons iconName="add" size="sm" />
          {t('contacts.persona.create_option')}
          <StyledCreateOptionDescription data-automation="PersonaFilterCreateDescription">
            {t('contacts.persona.create_option_description')}
          </StyledCreateOptionDescription>
        </StyledCreateOption>
        {personas.length !== 0 ? <StyledSeparator /> : null}
        {filteredPersonas.map((persona) => (
          <PersonaTooltip persona={persona} key={persona.id}>
            <StyledPersonaOption
              isSelected={persona.id === selectedPersona?.id}
              onClick={() => handlePersonaSelect(persona)}
            >
              <StyledPersonaName>{persona.name}</StyledPersonaName>
              {persona.isShared ? <StyledSharedIcon iconName="similar-icon" size="xs" /> : null}
              {!persona.isShared ? (
                <StyledEditButton onClick={(e) => handleClickOnEdit(e, persona)}>
                  <SWReactIcons iconName="settings" size="sm" />
                </StyledEditButton>
              ) : null}
            </StyledPersonaOption>
          </PersonaTooltip>
        ))}
      </Dropdown>
      <PersonaModal persona={persona} onClose={() => setPersona(null)} />
    </StyledFilterContainer>
  );
};
